"use client";

import type React from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  Text,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Image,
  Tabs,
  TabList,
  Tab,
  Badge,
  useBreakpointValue,
  Divider,
} from "@chakra-ui/react";
import { FiLogOut, FiChevronDown } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const profile = {
    full_name: user?.user_metadata?.full_name || null,
    email: user?.email || null,
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const getTabIndex = () => {
    if (location.pathname === "/onboarding-forms") return 0;
    if (location.pathname === "/billing") return 1;
    return 0;
  };

  return (
    // PAGE BG → WHITE
    <Box minH="100vh" bg="white">
      {/* Top Navigation */}
      <Box
        position="sticky"
        top={0}
        zIndex={20}
        bgGradient="linear-gradient(135deg, #050715 0%, #131633 50%, #050816 100%)"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        boxShadow="0 8px 24px rgba(0,0,0,0.45)"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          bg: "radial-gradient(circle at 0% 0%, rgba(0, 200, 255, 0.18) 0%, transparent 55%), radial-gradient(circle at 100% 0%, rgba(138, 43, 226, 0.16) 0%, transparent 55%)",
          opacity: 0.9,
          pointerEvents: "none",
        }}
      >
        <Box
          maxW="1200px"
          mx="auto"
          px={{ base: 4, md: 6 }}
          py={3}
          position="relative"
        >
          <Flex
            align="center"
            justify="space-between"
            gap={4}
            position="relative"
            zIndex={1}
          >
            {/* LEFT: Logo + Brand (icon styling as before) */}
            <HStack spacing={4} minW={0}>
              <HStack spacing={3} minW={0}>
                <Image
                  src="/main-logo.png"
                  alt="CallSupplai Logo"
                  h="30px"
                  objectFit="contain"
                  draggable={false}
                />
                <Box minW={0}>
                  <HStack spacing={2} align="center">
                    <Text
                      fontSize={{ base: "lg", md: "xl" }}
                      fontWeight="semibold"
                      bgGradient="linear-gradient(120deg, #0DD9FF, #A855F7, #F97316)"
                      bgClip="text"
                      letterSpacing="0.03em"
                      isTruncated
                    >
                      White Label Setup
                    </Text>
                    <Badge
                      variant="subtle"
                      colorScheme="purple"
                      borderRadius="full"
                      px={2.5}
                      py={0.5}
                      fontSize="0.65rem"
                    >
                      ONBOARDING
                    </Badge>
                  </HStack>
                  <Text
                    fontSize="xs"
                    color="whiteAlpha.700"
                    mt={0.5}
                    noOfLines={2}
                  >
                    Configure your own White-Labeled AI Voice Agent Application by
                    providing the necessary details.
                  </Text>
                </Box>
              </HStack>
            </HStack>

            {/* CENTER: Tabs (no pill rounding) */}
            {!isMobile && (
              <Tabs
                index={getTabIndex()}
                onChange={(index) => {
                  if (index === 0) navigate("/onboarding-form");
                  if (index === 1) navigate("/billing");
                }}
                variant="unstyled"
              >
                <TabList
                  bg="whiteAlpha.100"
                  borderRadius="md"
                  p="4px"
                  backdropFilter="blur(10px)"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                >
                  <Tab
                    px={4}
                    py={1.5}
                    fontSize="sm"
                    borderRadius="md"
                    color="whiteAlpha.800"
                    _selected={{
                      bg: "whiteAlpha.900",
                      color: "gray.900",
                      boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
                    }}
                    _hover={{ bg: "whiteAlpha.200" }}
                    fontWeight="500"
                  >
                    Complete Your Setup
                  </Tab>
                  <Tab
                    px={4}
                    py={1.5}
                    fontSize="sm"
                    borderRadius="md"
                    color="whiteAlpha.800"
                    _selected={{
                      bg: "whiteAlpha.900",
                      color: "gray.900",
                      boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
                    }}
                    _hover={{ bg: "whiteAlpha.200" }}
                    fontWeight="500"
                  >
                    Billing
                  </Tab>
                </TabList>
              </Tabs>
            )}

            {/* RIGHT: User Menu */}
            <HStack spacing={3} flexShrink={0}>
              {isMobile && (
                <Tabs
                  size="sm"
                  index={getTabIndex()}
                  onChange={(index) => {
                    if (index === 0) navigate("/onboarding-form");
                    if (index === 1) navigate("/billing");
                  }}
                  variant="unstyled"
                >
                  <TabList
                    bg="whiteAlpha.100"
                    borderRadius="md"
                    p="2px"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Tab
                      fontSize="xs"
                      px={3}
                      borderRadius="md"
                      color="whiteAlpha.800"
                      _selected={{ bg: "whiteAlpha.900", color: "gray.900" }}
                    >
                      Setup
                    </Tab>
                    <Tab
                      fontSize="xs"
                      px={3}
                      borderRadius="md"
                      color="whiteAlpha.800"
                      _selected={{ bg: "whiteAlpha.900", color: "gray.900" }}
                    >
                      Billing
                    </Tab>
                  </TabList>
                </Tabs>
              )}

              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="ghost"
                  rightIcon={<Icon as={FiChevronDown} />}
                  color="white"
                  _hover={{ bg: "whiteAlpha.100" }}
                  _active={{ bg: "whiteAlpha.200" }}
                  borderRadius="full"
                  px={2}
                >
                  <HStack spacing={2} maxW="160px">
                    <Avatar
                      size="xs"
                      name={profile?.full_name || profile?.email || "User"}
                      bg="gray.900"
                      color="white"
                    />
                    <Box textAlign="left" minW={0}>
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        isTruncated
                        maxW="110px"
                      >
                        {profile?.full_name ||
                          profile?.email?.split("@")[0] ||
                          "User"}
                      </Text>
                      {profile?.email && (
                        <Text
                          fontSize="0.65rem"
                          color="whiteAlpha.700"
                          isTruncated
                          maxW="110px"
                        >
                          {profile.email}
                        </Text>
                      )}
                    </Box>
                  </HStack>
                </MenuButton>
                <MenuList
                  bg="gray.900"
                  borderColor="whiteAlpha.200"
                  boxShadow="xl"
                  borderRadius="lg"
                  py={1}
                  minW="230px"
                >
                  <Box px={3} pt={2} pb={1}>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color="whiteAlpha.900"
                      noOfLines={1}
                    >
                      {profile?.full_name ||
                        profile?.email?.split("@")[0] ||
                        "Account"}
                    </Text>
                    {profile?.email && (
                      <Text fontSize="xs" color="whiteAlpha.600" noOfLines={1}>
                        {profile.email}
                      </Text>
                    )}
                  </Box>
                  <Divider borderColor="whiteAlpha.200" />
                  <MenuItem
                    onClick={handleSignOut}
                    color="red.300"
                    bg="blackAlpha.500"
                    _hover={{ bg: "red.500", color: "white" }}
                    fontSize="sm"
                    icon={<Icon as={FiLogOut} />}
                  >
                    Sign Out
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        </Box>
      </Box>

      {/* Main Content — now full-width white under the header */}
      <Box
        as="main"
        bg="white"
        maxW="1200px"
        mx="auto"
        px={{ base: 4, md: 6 }}
        pt={4}
        pb={{ base: 6, md: 8 }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
